import { useCallback, useEffect, useState } from "react";
import type { PhotoSource } from "@eagledispatch/shared";
import { apiFetch, API_URL } from "../../lib/api";
import Card from "../Card";

/**
 * LoadPhoto — wire shape of a job_photos metadata row (SPEC-54 extraction 1).
 * Moved here verbatim from all three office-detail consumers; exported so the
 * consumers import it instead of declaring three local copies.
 */
export type LoadPhoto = {
  id: string;
  kind: string;
  photoSource: PhotoSource;
  uploadedAt: string;
  path: string;
};

/**
 * LoadPhotosSection — the LOAD-photos gallery (SPEC-54 extraction 1).
 *
 * Hoisted token-identically from OfficeActiveJobDetail + FinishedJobDetail +
 * ReviewedJobDetail (v3 ground truth): `loadPhotos/loadPhotoLoading/
 * loadPhotoError/lightboxUrl` state family, `GET /jobs/:id/photos` fetch with
 * the client-side `p.kind === "LOAD"` filter, gallery grid, URL-string
 * lightbox overlay (`role="dialog"` / `aria-modal="true"`).
 *
 * Renders a bare `<Card>`; grid wrappers (`office-col`) stay inline in the
 * consumers. The three status strings (loading / failed / none) are preserved
 * verbatim from the originals.
 */
export default function LoadPhotosSection({ jobId }: { jobId: string }) {
  const [loadPhotos, setLoadPhotos] = useState<LoadPhoto[]>([]);
  const [loadPhotoLoading, setLoadPhotoLoading] = useState(false);
  const [loadPhotoError, setLoadPhotoError] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const fetchLoadPhotos = useCallback(async () => {
    setLoadPhotoLoading(true);
    setLoadPhotoError(false);
    try {
      const res = await apiFetch<{ photos: LoadPhoto[] }>(
        `/api/v1/jobs/${jobId}/photos`,
      );
      setLoadPhotos(res.photos.filter((p) => p.kind === "LOAD"));
    } catch {
      setLoadPhotoError(true);
    } finally {
      setLoadPhotoLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    void fetchLoadPhotos();
  }, [fetchLoadPhotos]);

  return (
    <>
      <Card>
        <h2>Load Photos</h2>
        {loadPhotoLoading ? (
          <p className="muted">Loading photos…</p>
        ) : loadPhotoError ? (
          <p className="err">Failed to load photos.</p>
        ) : loadPhotos.length === 0 ? (
          <p className="muted">No load photos yet.</p>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
              gap: "10px",
            }}
          >
            {loadPhotos.map((photo) => (
              <div
                key={photo.id}
                style={{
                  position: "relative",
                  borderRadius: "8px",
                  overflow: "hidden",
                  border: "1px solid var(--border)",
                }}
              >
                <img
                  src={`${API_URL}${photo.path}`}
                  alt="Load photo thumbnail"
                  style={{
                    width: "100%",
                    height: "96px",
                    objectFit: "cover",
                    cursor: "zoom-in",
                  }}
                  onClick={() => setLightboxUrl(`${API_URL}${photo.path}`)}
                />
                {photo.photoSource === "GALLERY" && (
                  <span
                    className="tag tag-red"
                    style={{
                      position: "absolute",
                      top: "4px",
                      left: "4px",
                      fontSize: "10px",
                      padding: "2px 6px",
                    }}
                  >
                    Gallery
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {lightboxUrl && (
        <div
          className="lightbox-overlay"
          onClick={() => setLightboxUrl(null)}
          role="dialog"
          aria-modal="true"
        >
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="lightbox-close"
              onClick={() => setLightboxUrl(null)}
            >
              ✕ Close
            </button>
            <img
              src={lightboxUrl}
              alt="Load photo enlarged"
              style={{ maxWidth: "90vw", maxHeight: "85vh" }}
            />
          </div>
        </div>
      )}
    </>
  );
}